import { Helmet } from "react-helmet-async";
import { ChevronDown } from "lucide-react";

// FAQ list built on native <details>/<summary> instead of the Radix
// accordion: Radix never renders a closed panel's children, so answers were
// missing from the prerendered HTML that crawlers and AI engines read (they
// only existed in JSON-LD). <details> keeps every answer in the page, works
// with browser find-in-page, and `name` makes a group exclusive (one open at
// a time) in browsers that support it.
//
// items: [{ q, a, extra? }] - `a` is plain text (also used for JSON-LD);
// `extra` is optional JSX appended to the visible answer only.
export default function FaqList({ items, group, variant = "muted", testId = "faq-item" }) {
  const base = variant === "white" ? "bg-white mb-card-shadow" : "bg-[#F4F6FB] open:bg-white";
  return (
    <div className="w-full">
      {items.map((f) => (
        <details
          key={f.q}
          name={group}
          data-testid={testId}
          className={`group border border-[#E6E9F1] rounded-xl mb-3 px-6 transition-colors open:border-[#FF6B00]/40 ${base}`}
        >
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 [&::-webkit-details-marker]:hidden">
            <h3 className="text-left text-base font-semibold text-[#0B1533] group-hover:text-[#FF6B00]">{f.q}</h3>
            <ChevronDown className="h-4 w-4 shrink-0 text-[#94A3B8] transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <div className="pb-5 text-sm text-[#475569] leading-relaxed">
            {f.a}
            {f.extra}
          </div>
        </details>
      ))}
    </div>
  );
}

export const faqJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Titled FAQ block with its FAQPage JSON-LD - for pages that have exactly
// one FAQ section. Pages with several (e.g. /faq) build one schema instead.
export function FaqSection({ title = "Frequently asked questions", overline, items, id = "faq", schema = true, tinted, children }) {
  if (!items?.length) return null;
  return (
    <section id={id} className={`py-20 md:py-24 border-t border-[#E6E9F1] ${tinted ? "bg-[#F4F6FB]" : "bg-white"}`} data-testid="faq-section">
      {schema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqJsonLd(items))}</script>
        </Helmet>
      )}
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-8">
        {overline && (
          <span className="inline-block uppercase text-xs font-semibold tracking-[0.2em] text-[#FF6B00]">{overline}</span>
        )}
        <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tighter text-[#0B1533]">{title}</h2>
        <div className="mt-8">
          <FaqList items={items} group={id} variant={tinted ? "white" : "muted"} />
        </div>
        {children}
      </div>
    </section>
  );
}
